 
/* eslint-disable @typescript-eslint/no-unused-vars */
import { EMPLOYEE_FORMULAS } from '@/config/gameFormulas';
import { EMPLOYEE_CONSTANTS } from '@/config/gameConstants';
import type { Employee } from '@/types/tycoon';
import type { Product, DevelopmentPhase } from '@/types/product';

export interface EmployeeAllocation {
  productId: string;
  phase: DevelopmentPhase | 'production';
  employeeType: string;
  count: number;
  employeeIds?: string[];
}

export interface EmployeeAvailability {
  [employeeType: string]: {
    total: number;
    allocated: number;
    available: number;
  };
}

export class EmployeeAllocationService {
  private static instance: EmployeeAllocationService;
  
  private constructor() {}
  
  static getInstance(): EmployeeAllocationService {
    if (!EmployeeAllocationService.instance) {
      EmployeeAllocationService.instance = new EmployeeAllocationService();
    }
    return EmployeeAllocationService.instance;
  }

  /**
   * Calculate the number of employees required for a development stage
   */
  calculateStageRequirement(
    stage: DevelopmentPhase | 'production',
    techScore: number,
    currentYear: number
  ): number {
    return EMPLOYEE_FORMULAS.getStageEmployeeRequirement(techScore, currentYear, stage);
  }

  /**
   * Get the employee type required for a stage
   */
  getRequiredEmployeeType(stage: DevelopmentPhase | 'production'): string {
    return EMPLOYEE_FORMULAS.getStageEmployeeType(stage);
  }

  /**
   * Calculate available employees by type
   */
  getEmployeeAvailability(
    employees: Employee[],
    allocations: EmployeeAllocation[]
  ): EmployeeAvailability {
    const availability: EmployeeAvailability = {};

    // Count total employees by type
    employees.forEach(employee => {
      if (!availability[employee.role]) {
        availability[employee.role] = { total: 0, allocated: 0, available: 0 };
      }
      availability[employee.role].total++;
    });

    // Count allocated employees
    allocations.forEach(allocation => {
      if (availability[allocation.employeeType]) {
        availability[allocation.employeeType].allocated += allocation.count;
      }
    });

    // Calculate available employees
    Object.keys(availability).forEach(type => {
      availability[type].available = availability[type].total - availability[type].allocated;
    });

    return availability;
  }

  /**
   * Check if there are enough employees available for a stage
   */
  canAllocateEmployees(
    stage: DevelopmentPhase | 'production',
    techScore: number,
    currentYear: number,
    employees: Employee[],
    allocations: EmployeeAllocation[]
  ): { canAllocate: boolean; reason?: string; required: number; available: number } {
    const required = this.calculateStageRequirement(stage, techScore, currentYear);
    const requiredType = this.getRequiredEmployeeType(stage);
    const availability = this.getEmployeeAvailability(employees, allocations);
    
    const typeAvailability = availability[requiredType] || { total: 0, allocated: 0, available: 0 };
    const available = typeAvailability.available;

    if (available < required) {
      const roleDisplayName = this.getRoleDisplayName(requiredType);
      return {
        canAllocate: false,
        reason: `Insufficient ${roleDisplayName}s. Required: ${required}, Available: ${available}`,
        required,
        available
      };
    }

    return { canAllocate: true, required, available };
  }

  /**
   * Allocate employees to a product stage
   */
  allocateEmployees(
    productId: string,
    stage: DevelopmentPhase | 'production',
    techScore: number,
    currentYear: number,
    allocations: EmployeeAllocation[]
  ): EmployeeAllocation {
    const required = this.calculateStageRequirement(stage, techScore, currentYear);
    const requiredType = this.getRequiredEmployeeType(stage);

    return {
      productId,
      phase: stage,
      employeeType: requiredType,
      count: required
    };
  }

  /**
   * Release employees from a product/stage
   */
  releaseEmployees(
    productId: string,
    stage?: DevelopmentPhase | 'production',
    allocations: EmployeeAllocation[]
  ): EmployeeAllocation[] {
    return allocations.filter(allocation => {
      if (allocation.productId !== productId) return true;
      if (stage && allocation.phase !== stage) return true;
      return false;
    });
  }

  /**
   * Get allocations for a specific product
   */
  getProductAllocations(
    productId: string,
    allocations: EmployeeAllocation[]
  ): EmployeeAllocation[] {
    return allocations.filter(allocation => allocation.productId === productId);
  }

  /**
   * Get total allocated employees for a product
   */
  getProductEmployeeCount(
    productId: string,
    allocations: EmployeeAllocation[]
  ): number {
    return this.getProductAllocations(productId, allocations)
      .reduce((total, allocation) => total + allocation.count, 0);
  }

  /**
   * Check if a product stage is paused due to insufficient employees
   */
  isStagePaused(
    product: Product,
    employees: Employee[],
    allocations: EmployeeAllocation[]
  ): boolean {
    const currentStage = product.status as DevelopmentPhase | 'production';
    const stageAllocation = allocations.find(
      a => a.productId === product.id && a.phase === currentStage
    );

    if (!stageAllocation) return false;

    // Count actual employees of the required type
    const actualCount = employees.filter(e => e.role === stageAllocation.employeeType).length;
    const allocatedToOthers = allocations
      .filter(a => a.productId !== product.id && a.employeeType === stageAllocation.employeeType)
      .reduce((sum, a) => sum + a.count, 0);
    
    const availableForThisProduct = actualCount - allocatedToOthers;
    
    return availableForThisProduct < stageAllocation.count;
  }

  /**
   * Get display name for a role
   */
  private getRoleDisplayName(roleId: string): string {
    const roleMap: Record<string, string> = {
      engineer: 'Engineer',
      designer: 'Designer',
      marketer: 'Marketer',
      manager: 'Manager',
      researcher: 'Researcher',
      tester: 'Tester',
      assemblyWorker: 'Assembly Worker'
    };
    return roleMap[roleId] || roleId;
  }

  /**
   * Clean up orphaned allocations - allocations for products that are no longer in the allocated phase
   */
  cleanupOrphanedAllocations(
    products: Product[],
    allocations: EmployeeAllocation[]
  ): EmployeeAllocation[] {
    return allocations.filter(allocation => {
      const product = products.find(p => p.id === allocation.productId);
      
      // If product doesn't exist, remove allocation
      if (!product) {
        console.log(`Cleaning up allocation for non-existent product ${allocation.productId}`);
        return false;
      }
      
      // If product is discontinued, remove all allocations
      if (product.status === 'discontinued') {
        console.log(`Cleaning up allocation for discontinued product ${product.name}`);
        return false;
      }
      
      // Check if the allocation phase matches the product's current status
      if (allocation.phase !== product.status) {
        console.log(`Cleaning up mismatched allocation: ${product.name} is in ${product.status} but has allocation for ${allocation.phase}`);
        return false;
      }
      
      // If product is in production, remove any development phase allocations
      const developmentPhases = ['design', 'engineering', 'testing', 'manufacturing'];
      if (product.status === 'production' && developmentPhases.includes(allocation.phase)) {
        console.log(`Cleaning up development phase allocation: ${product.name} is in production but has ${allocation.phase} allocation`);
        return false;
      }
      
      // Keep the allocation
      return true;
    });
  }

  /**
   * Get all allocations across all products (for debugging)
   */
  getAllocationSummary(allocations: EmployeeAllocation[]): Record<string, { total: number; byPhase: Record<string, number> }> {
    const summary: Record<string, { total: number; byPhase: Record<string, number> }> = {};
    
    allocations.forEach(allocation => {
      if (!summary[allocation.employeeType]) {
        summary[allocation.employeeType] = { total: 0, byPhase: {} };
      }
      
      summary[allocation.employeeType].total += allocation.count;
      
      if (!summary[allocation.employeeType].byPhase[allocation.phase]) {
        summary[allocation.employeeType].byPhase[allocation.phase] = 0;
      }
      summary[allocation.employeeType].byPhase[allocation.phase] += allocation.count;
    });
    
    return summary;
  }
}

export const employeeAllocationService = EmployeeAllocationService.getInstance();